import React from 'react';
import { Typography, IconButton, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { ScheduledGradeDTO } from '@beable/roster-backend-client-v1';
import { Check, ErrorOutlined, Autorenew } from '@mui/icons-material';
import { getStyles } from './styles';

interface AccessTreeProps {
    data: ScheduledGradeDTO[];
    reprovisionClass(classId: string): void;
}

export default function AccessTree({ data, reprovisionClass }: AccessTreeProps) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const isGradeAllScheduled = (scheduledGrade: ScheduledGradeDTO) => {
    return !scheduledGrade.rosterCourses.some(course => (
      course.classes.find(cls => cls.scheduled === false && cls.hasAccess === true)
    ));
  }

  const partialGradeScheduled = (scheduledGrade: ScheduledGradeDTO) => {
    return !scheduledGrade.hasAccess && scheduledGrade.rosterCourses.some((course) => course.hasAccess)
  }

  return (
    <SimpleTreeView sx={styles.root}>
      { data.map((scheduledGrade) => (
        (scheduledGrade.hasAccess || partialGradeScheduled(scheduledGrade)) &&
        <TreeItem
          key={scheduledGrade.grade.toString()}
          itemId={scheduledGrade.grade.toString()}
          label={
            <Box sx={styles.labelRoot}>
              <Typography variant="body2" sx={styles.labelText}>
                Grade {scheduledGrade.grade}
              </Typography>
              { !isGradeAllScheduled(scheduledGrade) &&
                <Typography variant="body2" sx={styles.labelIcon}>
                  <ErrorOutlined data-testid='svg-error-grade' fontSize="small"/>
                </Typography>
              }
            </Box>
          }
        >
          { scheduledGrade.rosterCourses.map(course => (
            course.hasAccess &&
            <TreeItem
              key={course.courseId}
              itemId={course.courseId}
              label={
                <Box sx={styles.labelRoot}>
                  <Typography variant="body2" sx={styles.labelText}>
                    {course.courseName} {course.courseCode ? ` (${course.courseCode})` : ''}
                  </Typography>
                  { course.classes.find(cls => cls.scheduled === false) &&
                    <Typography variant="body2" sx={styles.labelIcon}>
                      <ErrorOutlined data-testid='svg-error-course' fontSize="small"/>
                    </Typography>
                  }
                </Box>
              }
            >
            { course.classes.map(provClass => (
              provClass.hasAccess &&
              <TreeItem
                key={provClass.classId}
                itemId={provClass.classId}
                label={
                  <Box sx={styles.labelRoot}>
                  <Typography variant="body2" sx={styles.labelText}>
                    {provClass.className}
                  </Typography>
                  <Typography variant="body2" sx={styles.labelIcon}>
                    { provClass.scheduled ?
                      <Check data-testid='svg-check' fontSize="small"/> :
                      <ErrorOutlined data-testid='svg-error-class' fontSize="small"/>
                    }
                  </Typography>
                  <IconButton
                    aria-label="reprovision"
                    sx={styles.labelIcon}
                    onClick={() => reprovisionClass(provClass.classId)}
                    data-testid='btn-reprovision'
                    size="large">
                    <Autorenew fontSize="small" />
                  </IconButton>
                  </Box>
                }
                sx={provClass.scheduled ? undefined : styles.classRowFail}
              />
            ))}
            </TreeItem>
          ))}
        </TreeItem>
      ))}
    </SimpleTreeView>
  );
}
